import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { ApiDefaults, LsqListSource } from '@lsq-frontend/angular-shared/api-service';
import {
    CollectionInvoice,
    GetPurchaseInvoicesByRelationshipRequest,
    GetPurchaseInvoicesByRelationshipService,
    Purchase,
    PurchaseInvoice,
    PurchaseStatus
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { Color, StatusColor, TableAppearance, Utilities } from '@lsq-frontend/common-ui';
import {
    ActionSheetService,
    getRouterLinkUrlById,
    Icons,
    ResponsiveService
} from '@lsq-frontend/common-ui';
import { LsqExpandableDataRowComponent } from '@lsq-frontend/common-ui';
import { PopoverPlacement } from 'ngx-smart-popover';
import { of } from 'rxjs';
import { EnumDisplay } from '../../../../../constants/enum-display.constants';
import { RouteParams } from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    atlas,
    CUSTOMER_PURCHASES,
    INVOICES_INVOICE
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { RouteQueryParams } from '@lsq-frontend/angular-shared/app-constants';

@Component({
    selector: 'purchases-by-customer-expandable-row',
    template: `
        <div *ngIf="!isSmallDevice" class="purchases-expandable-group">
            <div class="expandable-row" *ngIf="dataSourceRow">
                <lsq-data-table
                    [dataSource]="dataSourceRow"
                    [appearance]="tableAppearance.ExpandableRow"
                    [layoutFixed]="true"
                    (click)="handleShowHideClick($event)"
                >
                    <lsq-column [column]="{ key: 'rowToggle', width: '4.7rem' }">
                        <ng-template [custom-cell] let-row="row">
                            <div class="expandable-row-toggle-cell">
                                <div class="expandable-row-toggle" *ngIf="rowIsExpandable">
                                    <button class="btn-transparent-box">
                                        <lsq-icon
                                            [type]="
                                                isExpanded ? icons.ChevronDown : icons.ChevronRight
                                            "
                                            color="black"
                                        ></lsq-icon>
                                        <span
                                            *ngIf="isExpanded"
                                            class="show-hide-text small-uppercase"
                                            i18n="Tells the user what to click to hide content"
                                        >
                                            Hide
                                        </span>
                                        <span
                                            *ngIf="!isExpanded"
                                            class="show-hide-text small-uppercase"
                                            i18n="Tells the user what to click to show content"
                                        >
                                            Show
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </ng-template>
                    </lsq-column>

                    <lsq-hidden-column
                        [column]="{ key: 'counterHidden' }"
                        width="4.8rem"
                    ></lsq-hidden-column>

                    <lsq-column [column]="{ key: 'batchNumber', width: '25%' }">
                        <ng-template [custom-cell] let-row="row">
                            <div class="upload-number-cell">
                                <span
                                    i18n="upload number table cell label"
                                    class="upload-number-label"
                                    >Upload
                                </span>
                                <span class="upload-number">#{{ row.batchNumber }}</span>
                            </div>
                        </ng-template>
                    </lsq-column>

                    <lsq-currency-column
                        [column]="{
                            key: 'purchaseAmount',
                            width: '13%',
                            sortable: false
                        }"
                    >
                    </lsq-currency-column>

                    <lsq-currency-column
                        [column]="{
                            key: 'deniedAmount',
                            width: '13%',
                            sortable: false
                        }"
                    >
                    </lsq-currency-column>

                    <lsq-currency-column
                        [column]="{
                            key: 'ineligibleAmount',
                            width: '13%',
                            sortable: false
                        }"
                    >
                    </lsq-currency-column>

                    <lsq-currency-column
                        [column]="{
                            key: 'purchaseFees',
                            width: '13%',
                            sortable: false
                        }"
                    >
                    </lsq-currency-column>

                    <lsq-column
                        [column]="{
                            key: 'statusDot',
                            sortable: false,
                            width: '6%'
                        }"
                    >
                        <ng-template [custom-cell] let-row="row">
                            <span
                                *ngIf="
                                    row.purchaseStatus &&
                                    statusColor[purchaseStatus[row.purchaseStatus]]
                                "
                                [ngStyle]="{
                                    float: 'right',
                                    paddingRight: '0.25rem'
                                }"
                            >
                                <lsq-icon
                                    [type]="icons.Circle"
                                    [color]="statusColor[purchaseStatus[row.purchaseStatus]]"
                                ></lsq-icon>
                            </span>
                        </ng-template>
                    </lsq-column>

                    <lsq-column
                        [column]="{
                            key: 'purchaseStatus',
                            className: 'popover-pad',
                            width: '9rem'
                        }"
                    >
                        <ng-template [custom-cell] let-row="row">
                            <div class="status-wrapper">
                                <span
                                    *ngIf="
                                        row.purchaseStatus &&
                                        enumDisplay.PurchasesStatus[row.purchaseStatus]
                                    "
                                >
                                    <span
                                        [innerHtml]="
                                            enumDisplay.PurchasesStatus[row.purchaseStatus]
                                        "
                                        class="status-col"
                                    >
                                    </span>
                                </span>
                                <span>
                                    <lsq-icon-popover
                                        *ngIf="
                                            enumDisplay.PurchasesStatusPopover[row.purchaseStatus]
                                        "
                                        [placement]="placement.TopLeft"
                                        [text]="
                                            enumDisplay.PurchasesStatusPopover[row.purchaseStatus]
                                        "
                                    >
                                    </lsq-icon-popover>
                                </span>
                            </div>
                        </ng-template>
                    </lsq-column>
                </lsq-data-table>
            </div>

            <lsq-circle-loader
                *ngIf="isExpanded && innerTableIsLoading"
                class="expanded-row-loader"
            >
            </lsq-circle-loader>

            <div class="expanded-table" *ngIf="isExpanded && dataSourceInner && rowIsExpandable">
                <lsq-data-table
                    [dataSource]="dataSourceInner"
                    [isPaginated]="false"
                    [hasHeader]="false"
                    [hasSpinner]="false"
                    [layoutFixed]="true"
                >
                    <lsq-hidden-column width="4.7rem"></lsq-hidden-column>

                    <lsq-hidden-column
                        [column]="{ key: 'spacer-hidden' }"
                        width="4.8rem"
                    ></lsq-hidden-column>

                    <lsq-row-count-column width="3%"></lsq-row-count-column>

                    <lsq-column
                        [column]="{
                            header: 'Invoice #',
                            className: 'e2e-invoice-number-col',
                            key: 'invoiceNumber',
                            truncateTextCss: true,
                            width: '17%'
                        }"
                    >
                        <ng-template [custom-cell] let-row="row">
                            <a
                                [routerLink]="['/invoices', row.invoiceKey]"
                                [ngStyle]="{ 'font-weight': 'bold' }"
                            >
                                {{ row.invoiceNumber }}
                            </a>
                        </ng-template>
                    </lsq-column>

                    <lsq-date-column
                        [column]="{
                            header: 'Invoice Date',
                            key: 'invoiceDate',
                            width: '5%'
                        }"
                    >
                    </lsq-date-column>

                    <lsq-currency-column
                        [column]="{
                            header: 'Amount',
                            key: 'invoiceAmount',
                            width: '13%',
                            sortable: false
                        }"
                    >
                    </lsq-currency-column>

                    <lsq-currency-column
                        [column]="{
                            header: 'Denied',
                            key: 'deniedAmount',
                            width: '13%',
                            sortable: false
                        }"
                    >
                    </lsq-currency-column>

                    <lsq-currency-column
                        [column]="{
                            header: 'Ineligibles',
                            key: 'ineligibleAmount',
                            width: '13%',
                            sortable: false
                        }"
                    >
                    </lsq-currency-column>

                    <lsq-currency-column
                        [column]="{
                            header: 'Fees',
                            key: 'purchaseFees',
                            width: '13%',
                            sortable: false
                        }"
                    >
                    </lsq-currency-column>

                    <lsq-column
                        [column]="{
                            key: 'statusDot',
                            sortable: false,
                            width: '6%'
                        }"
                    >
                        <ng-template [custom-cell] let-row="row">
                            <span
                                *ngIf="row.status && statusColor[row.status]"
                                [ngStyle]="{
                                    float: 'right',
                                    paddingRight: '0.25rem'
                                }"
                            >
                                <lsq-icon
                                    [type]="icons.Circle"
                                    [color]="statusColor[row.status]"
                                ></lsq-icon>
                            </span>
                        </ng-template>
                    </lsq-column>

                    <lsq-column
                        [column]="{
                            header: 'Status',
                            key: 'status',
                            className: 'popover-pad',
                            width: '9rem'
                        }"
                    >
                    </lsq-column>
                </lsq-data-table>
            </div>
        </div>

        <ng-template #actionSheetTemplate>
            <ng-container *ngIf="mobileDrawerRows">
                <div class="invoice-cards" iosNestedScroll>
                    <div
                        *ngFor="let invoice of mobileDrawerRows"
                        class="invoice-card mtc-header"
                        (click)="goToInvoice(invoice)"
                    >
                        <h2 class="mtc-header-title-only font-weight-bold">
                            Invoice {{ invoice.invoiceNumber }}
                        </h2>
                        <a class="mtc-header-chevron">
                            <lsq-icon [color]="color.Gray" [type]="icons.ChevronRight"> </lsq-icon>
                        </a>
                    </div>
                </div>
            </ng-container>
        </ng-template>
    `,
    styleUrls: ['./purchases-by-customer.expandable-row.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class PurchasesByCustomerExpandableRowComponent
    extends LsqExpandableDataRowComponent<Purchase, PurchaseInvoice>
    implements OnInit {
    @Input() public fromDateFilter: Date;
    @Input() public toDateFilter: Date;
    @Input() public debtorKey: number;

    // Constants.
    public color = Color;
    public enumDisplay = EnumDisplay;
    public icons = Icons;
    public placement = PopoverPlacement;
    public purchaseStatus = PurchaseStatus;
    public routeQueryParams = RouteQueryParams;
    public statusColor = StatusColor;
    public tableAppearance = TableAppearance;
    public utilities = Utilities;
    public purchasesRouterLink = getRouterLinkUrlById(atlas, CUSTOMER_PURCHASES);

    constructor(
        public router: Router,
        public getPurchaseInvoicesByRelationshipService: GetPurchaseInvoicesByRelationshipService,
        public responsiveService: ResponsiveService,
        public actionSheetService: ActionSheetService
    ) {
        super(responsiveService, actionSheetService);
    }

    public async handleInitialExpand(): Promise<void> {
        const response = await this.getPurchaseInvoicesByRelationshipService.executeOnce(
            new GetPurchaseInvoicesByRelationshipRequest({
                transKey: this.row.transKey,
                debtorKey: this.debtorKey,
                take: ApiDefaults.TakeAll
            })
        );
        this.dataSourceInner = new LsqListSource<PurchaseInvoice>({
            data$: of(response),
            rows$: of(response.rows)
        });

        // Set up context for mobile action sheet.
        this.mobileDrawerRows = response ? response.rows : [];
    }

    public async goToInvoice(invoice: CollectionInvoice): Promise<void> {
        await this.router.navigateByUrl(
            getRouterLinkUrlById(atlas, INVOICES_INVOICE).replace(
                RouteParams.InvoiceKey,
                `${invoice.invoiceKey}`
            )
        );
        this.actionSheetService.closeActionSheet();
    }
}
